import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { getFormularioCmdrsByUUID } from 'api/forms/pacote_formularios.api';
import ContainerPainel from 'components/container_painel';
import { FormCmdrsHorizontalStepper } from 'components/forms/form_cmdrs_horizontal_stepper';
import { Loading } from 'components/login';
import { useAuth } from 'contexts/auth/auth_context';
import { UserPermissionEnum } from 'enum/userPermission.enum';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import Step1CmdrsView from './steps/step1_view';
import Step2CmdrsView from './steps/step2_view';
import Step3CmdrsView from './steps/step3_view';
import Step4CmdrsView from './steps/step4_view';
import Step5CmdrsView from './steps/step5_view';
import { formatDate } from 'utils/format_date';
import Step7CmdrsView from './steps/step7_view';
import { getListUsersByFilters } from 'api/user/user.api';

async function fetchCmdrsFormData(uuid: string) {
    const data = await getFormularioCmdrsByUUID(uuid);

    return {
        step1: {
            hasCmdrs: data.has_cmdrs ?? null,
            instrument: data.instrument ?? '',
            instrumentNumber: data.instrument_number ?? '',
            publicationDate: formatDate(data.publication_date),
            document_uuid: data.document_uuid ?? '',
            status: data.status ?? '',
            structure: data.structure ?? '',
            presidingInstitution: data.presiding_institution ?? '',
            mandateEnd: formatDate(data.mandate_end),
            qtdPoderPublico: data.qtd_poder_publico ?? 0,
            qtdSociedadeCivil: data.qtd_sociedade_civil ?? 0,
        },
        step2: {
            members: data.members ?? [],
        },
        step3: {
            existingCouncils: data.existing_councils ?? [],
            otherExistingCouncil: data.other_existing_council ?? '',
            seatedCouncils: data.seated_councils ?? [],
            otherSeatedCouncil: data.other_seated_council ?? '',
            isConsortiumMember: typeof data.is_consortium_member === 'boolean' ? data.is_consortium_member : null,
            participatesActivities: typeof data.participates_activities === 'boolean' ? data.participates_activities : null,
            activitiesDescription: data.activities_description ?? '',
            hasSusaf: typeof data.has_susaf === 'boolean' ? data.has_susaf : null,
        },
        step4: {
            selectedChallenges: data.selected_challenges ?? [],
            challengesOrder: data.challenges_order ?? [],
            otherChallenge: data.other_challenge ?? '',
        },
        step5: {
            demands: data.demands ?? [],
        },
        created_by: data.created_by ?? '',
    };
}

const FormularioCMDRSVisualizar: React.FC = () => {
    const { uuid } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [formulario, setFormulario] = useState<any>(null);
    const [responsible, setResponsible] = useState({
        responsibleName: '',
        responsibleRole: '',
        responsibleContact: '',
    });

    const usuariosPermitidos = [
        UserPermissionEnum.STATE_MANAGER,
        UserPermissionEnum.MUNICIPAL_MANAGER,
        UserPermissionEnum.ADMIN,
    ];

    const isUsuarioPermitido = usuariosPermitidos.includes(
        user?.permission.name as UserPermissionEnum,
    );

    const getFormulario = async () => {
        try {
            setHasError(false);
            setErrorMessage('');

            if (!uuid) {
                setHasError(true);
                setErrorMessage('UUID do formulário não encontrado');
                return;
            }

            const formData = await fetchCmdrsFormData(uuid);

            // Verificar se há dados preenchidos
            const temDadosPreenchidos = Object.values(formData).some((value: any) =>
                value !== null && value !== undefined && value !== '' &&
                (Array.isArray(value) ? value.length > 0 : true)
            );

            if (!temDadosPreenchidos) {
                setHasError(true);
                setErrorMessage('Nenhum dado preenchido disponível para este município');
                return;
            }

            setFormulario(formData);
        } catch (error) {
            console.error('Erro ao buscar formulário:', error);
            setHasError(true);
            setErrorMessage('Município sem informação enviada para visualização pelo Gerente Estadual');
        }
    };

    useEffect(() => {
        if (uuid) {
            getFormulario();
        }

        // Cleanup ao sair da tela
        return () => {
            setFormulario(null);
        };
    }, [uuid]);

    useEffect(() => {
        const responsibleUuid = formulario?.created_by;
        if (responsibleUuid) {
            getListUsersByFilters({
                limit: '1',
                uuid: responsibleUuid,
            }).then((res: any) => {
                const user = res?.data?.[0];
                setResponsible({
                    responsibleName: user?.name || '',
                    responsibleRole: user?.position || user?.occupation || '',
                    responsibleContact: user?.contactList?.[0]?.value || '',
                });
            });
        }
    }, [formulario?.step1?.createdBy, formulario?.created_by]);

    const renderStepContent = () => {
        if (hasError) {
            return (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Alert severity="warning" sx={{ maxWidth: 600 }}>
                        <Typography variant="body1">
                            {errorMessage}
                        </Typography>
                    </Alert>
                </Box>
            );
        }

        if (!formulario) {
            return (
                <Box display="flex" justifyContent="center">
                    <Loading />
                </Box>
            );
        }

        switch (currentStep) {
            case 1:
                return <Step1CmdrsView data={formulario.step1} />;
            case 2:
                return <Step2CmdrsView data={formulario.step2} />;
            case 3:
                return <Step3CmdrsView data={formulario.step3} />;
            case 4:
                return <Step4CmdrsView data={formulario.step4} />;
            case 5:
                return <Step5CmdrsView data={formulario.step5} />;
            default:
                return <Step1CmdrsView data={formulario.step1} />;
        }
    };

    return (
        <ContainerPainel
            pagina='Visualizar Formulário do CMDRS'
            showBackButton={true}
        >
            {isUsuarioPermitido ? (
                <Stack marginX={'4rem'} paddingTop={'2rem'}>
                    <FormCmdrsHorizontalStepper
                        activeStep={currentStep}
                        edit={false}
                        hasCmdrs={formulario?.step1.hasCmdrs ?? null}
                    />

                    <Box mt={2}>
                        {renderStepContent()}
                    </Box>

                    {/* Navegação entre abas */}
                    {!hasError && formulario && (
                        <Box display="flex" justifyContent="space-between" mt={4} mb={2}>
                            <Button
                                variant="outlined"
                                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                                disabled={currentStep === 1}
                            >
                                Aba Anterior
                            </Button>

                            <Typography variant="body2" color="textSecondary" alignSelf="center">
                                Aba {currentStep} de 5
                            </Typography>

                            <Button
                                variant="outlined"
                                onClick={() => setCurrentStep(Math.min(7, currentStep + 1))}
                                disabled={currentStep === 5}
                            >
                                Próxima Aba
                            </Button>
                        </Box>
                    )}
                </Stack>
            ) : (
                <Stack
                    sx={{
                        marginX: '4rem',
                        paddingTop: '2rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        backgroundColor: '#FFF',
                        borderRadius: '1rem',
                        padding: '2rem',
                        margin: '2rem',
                    }}
                >
                    <Typography variant="h6" color="error">
                        Acesso Negado
                    </Typography>
                    <Typography>
                        Você não tem permissão para visualizar este formulário
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate(ROUTES.PACOTE_FORMULARIOS)}
                    >
                        Voltar
                    </Button>
                </Stack>
            )}
        </ContainerPainel>
    );
};

export default FormularioCMDRSVisualizar;